import { Component, OnDestroy, OnInit } from '@angular/core';
import { SystemNoteEdit } from '../model/systemNoteEdit';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { SystemNoteService } from 'src/app/core/service/system-note.service';
import { SystemNoteListService } from 'src/app/core/service/grid-data/grid-data-system-note.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-system-note-list',
  templateUrl: './system-note-list.component.html',
  styleUrls: ['./system-note-list.component.scss']
})
export class SystemNoteListComponent implements OnInit, OnDestroy
{
  public pageSizes = [5, 10, 25, 50, 100];
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: SystemNoteEdit;
  public selectedSystemNote: SystemNoteEdit;
  public filterText = '';
  public maxTextLength = 100;
  public editSystemNote = false;

  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'lastModifiedOn', dir: 'desc' }
    ]
  };

  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;

  private listSubscription: Subscription;

  /**
   * Constructor
   */
  constructor(
    private systemNoteService: SystemNoteService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private systemNoteListService: SystemNoteListService
  )
  {
  }

  public get loading(): boolean
  {
    return this.systemNoteListService.loading;
  }

  public ngOnInit(): void
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.listSubscription = this.systemNoteListService.subscribe({
      next: (value: any) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            d.lastModifiedOn = new Date(d.lastModifiedOn);
          });
          this.gridView = {
            data: (value) ? this.gridData = value.data : this.gridData = [],
            total: (value) ? this.total = value.total : this.total = 0
          };
        }
      }
    });
    this.systemNoteListService.queryByState(this.state);
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const systemNoteId = this.selectedSystemNote.id;
      this.systemNoteService.deleteSystemNote(systemNoteId).subscribe({
        next: () =>
        {
          this.systemNoteListService.queryByState(this.state);
          this.messageHandlerService.displaySuccess('SYSTEM-NOTE-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedSystemNote = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.systemNoteListService.queryByState(state);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    this.systemNoteService.getSystemNoteById(id).subscribe({
      next: (data) =>
      {
        this.selectedSystemNote = data as SystemNoteEdit;
        this.open('deleteDialog');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new SystemNoteEdit();
    this.editSystemNote = true;
  }

  public editHandler(systemNote): void
  {
    this.isNew = false;
    this.systemNoteService.getSystemNoteById(systemNote.id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
        this.selectedSystemNote = data;
        this.editSystemNote = true;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(systemNote: SystemNoteEdit): void
  {
    this.systemNoteService.saveSystemNote(systemNote, this.isNew, systemNote.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedOn into Date object
        data.lastModifiedOn = new Date(data.lastModifiedOn);

        this.logger.info('saved', systemNote);

        this.editDataItem = undefined;

        this.systemNoteListService.queryByState(this.state);
        this.messageHandlerService.displaySuccess('SYSTEM-NOTE-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }


  /**
   * Event called after save in order to perform grid refresh.
   */
  public afterSaveHandler(): void
  {
    this.systemNoteListService.queryByState(this.state);
    this.editSystemNote = null;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.systemNoteListService.queryByState(this.state);
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state, 'text');
    this.filterText = filterText;
    this.filterChange(root);
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }
}
